import { Step4FormProps, WizzardStep4Form } from '../types'
import { useForm } from 'react-hook-form'
import * as yup from 'yup'
import { yupResolver } from '@hookform/resolvers/yup'
import React, { useCallback, useMemo } from 'react'
import { DefaultValue } from '../constants'
import useActivationObject from '../../../providers/ActivationObjectProvider/hooks/useActivationObject'
import { ActivationObject, ContactPerson } from '../../../providers/ActivationObjectProvider/types'
import { VALIDATIONPATTERN_EMAIL, VALIDATIONPATTERN_PHONE, VALIDATION_REQUIRED } from '../../../../constants/validation'
import useStep4FormContactPersons from './useStep4.form.contactPersons'

const useStep4Form = (activationObject?: ActivationObject): Step4FormProps => {
    const propName = (name: keyof WizzardStep4Form): string => name
    const contactPersonPropName = (name: keyof ContactPerson): string => name

    const validationSchema = useMemo(
        () =>
            yup.object().shape({
                [propName('complyWithGuidelines')]: yup.boolean().required(VALIDATION_REQUIRED),
                [propName('comunicationPassword')]: yup
                    .string()
                    .nullable()
                    .when({
                        is: (val: string | null) => val && val.length > 0,
                        then: yup
                            .string()
                            .min(3, 'Heslo musí mít minimálně 3 znaky')
                            .max(15, 'Heslo musí mít maximálně 15 znaků'),
                    }),
                [propName('appMyJablotronEmail')]: activationObject?.canEditAppMyJablotronEmail
                    ? yup
                          .string()
                          .required(VALIDATION_REQUIRED)
                          .email('Špatný formát emailové adresy')
                          .matches(VALIDATIONPATTERN_EMAIL, 'Špatný formát emailové adresy')
                    : yup.string().nullable(),
                [propName('personContacts')]: yup
                    .array()
                    .of(
                        yup.object().shape({
                            [contactPersonPropName('fullName')]: yup.string().required(VALIDATION_REQUIRED),
                            [contactPersonPropName('email')]: yup
                                .string()
                                .nullable()
                                .when('creatAccountToZZ', {
                                    is: true,
                                    then: yup
                                        .string()
                                        .required('Toto pole je povinné, chcete-li založit účet na ZZ')
                                        .email('Špatný formát emailové adresy')
                                        .matches(VALIDATIONPATTERN_EMAIL, 'Špatný formát emailové adresy'),
                                }),
                            [contactPersonPropName('phone')]: yup
                                .string()
                                .required(VALIDATION_REQUIRED)
                                .matches(VALIDATIONPATTERN_PHONE, 'Špatný formát tel. čísla'),
                        })
                    )
                    .test('oneAdmin', 'Minimálně jedna kontaktní osoba musí být Administrátor', (value, context) => {
                        return value !== undefined && value.findIndex((pers) => pers.role === 'Admin') !== -1
                    }),
            }),
        [activationObject?.canEditAppMyJablotronEmail]
    )

    const form = useForm<WizzardStep4Form>({
        criteriaMode: 'all',
        reValidateMode: 'onChange',
        resolver: yupResolver(validationSchema),
        defaultValues: DefaultValue,
    })
    const contactPersonsProps = useStep4FormContactPersons(form, activationObject)

    const reset = form.reset
    React.useEffect(() => {
        if (activationObject) {
            reset({
                complyWithGuidelines: activationObject.complyWithGuidelines ?? DefaultValue.complyWithGuidelines,
                comunicationPassword: activationObject.comunicationPassword ?? DefaultValue.comunicationPassword,
                appMyJablotronEmail: activationObject.appMyJablotronEmail ?? DefaultValue.appMyJablotronEmail,
                personContacts: activationObject.personContacts ?? DefaultValue.personContacts,
            })
        }
    }, [activationObject, reset])

    const { update } = useActivationObject()
    const onSubmit = useCallback(
        (data: WizzardStep4Form) => {
            if (activationObject) {
                const newObject: ActivationObject = {
                    ...activationObject,
                    ...data,
                    wizardStep: 'Invoicing',
                }
                update(newObject)
            }
        },
        [activationObject, update]
    )

    return {
        form,
        onSubmit,
        contactPersons: contactPersonsProps,
    }
}

export default useStep4Form
